import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { makeCaptcha, type Captcha } from "@/lib/captcha";
import { useT } from "@/i18n";

export function CaptchaField({
  id = "captcha",
  value,
  onChange,
  onValidChange,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onValidChange?: (valid: boolean) => void;
}) {
  const { t } = useT();
  const [captcha, setCaptcha] = useState<Captcha | null>(null);

  useEffect(() => {
    setCaptcha(makeCaptcha());
    onChange("");
  }, [onChange]);

  const valid = useMemo(
    () => (captcha ? Number(value) === captcha.answer : false),
    [value, captcha],
  );

  useEffect(() => {
    onValidChange?.(valid);
  }, [valid, onValidChange]);

  function refresh() {
    setCaptcha(makeCaptcha());
    onChange("");
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {captcha
          ? t("shell.captcha.question", { a: captcha.a, op: captcha.op === "*" ? "×" : captcha.op, b: captcha.b })
          : t("shell.captcha.loading")}
      </Label>
      <div className="flex gap-2">
        <Input
          id={id}
          inputMode="numeric"
          autoComplete="off"
          required
          disabled={!captcha}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d-]/g, ""))}
          placeholder={t("shell.captcha.answerPlaceholder")}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={refresh}
          disabled={!captcha}
          aria-label={t("shell.captcha.newChallenge")}
          title={t("shell.captcha.newChallenge")}
        >
          <RefreshCw className="size-4" />
        </Button>
      </div>
    </div>
  );
}
